import React, { useState } from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import SEO from '../components/SEO';
import PayPalButton from '../components/PayPalButton';
import MpesaButton from '../components/MpesaButton';
import PaymentStatusModal from '../components/PaymentStatusModal';

const DonatePage = () => {
  const [selectedAmount, setSelectedAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // PayPal configuration
  const paypalInitialOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "capture",
    "data-client-token": undefined, // Remove this to avoid URI issues
    "disable-funding": "", // Clear this to avoid encoding issues
    "enable-funding": "" // Clear this to avoid encoding issues
  };

  console.log('🔧 PayPal Config:', {
    clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
    environment: process.env.REACT_APP_PAYPAL_ENVIRONMENT
  });

  // Predefined amounts for both currencies
  const predefinedAmounts = {
    USD: [10, 25, 50, 100, 250, 500],
    KES: [1000, 2500, 5000, 10000, 25000, 50000]
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const amount = e.target.value;
    setCustomAmount(amount);
    setSelectedAmount('');
  };

  const handleCurrencyToggle = (currency) => {
    setSelectedCurrency(currency);
    setSelectedAmount('');
    setCustomAmount('');
    setPhoneNumber(''); // Clear phone number when switching currency
  };

  const getFinalAmount = () => {
    return parseFloat(customAmount) || selectedAmount;
  };

  const handlePaymentSuccess = (details) => {
    console.log('Payment successful:', details);
    
    if (details.pending) {
      // M-Pesa payment pending - show modal
      setPaymentData(details);
      setShowPaymentModal(true);
    } else {
      // PayPal or confirmed M-Pesa payment - redirect to success
      window.location.href = '/donation/success';
    }
  };

  const handlePaymentError = (error) => {
    console.error('💥 Payment error in DonatePage:', error);
    
    // Close modal if open
    setShowPaymentModal(false);
    
    // Show user-friendly error message
    let errorMessage = error.message || 'Payment failed. Please try again.';
    
    // Check for specific error types
    if (errorMessage.includes('backend server') || errorMessage.includes('No response')) {
      errorMessage = 'Unable to connect to payment server. Please check if the backend server is running and try again.';
    } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (errorMessage.includes('phone')) {
      errorMessage = 'Invalid phone number. Please enter a valid Safaricom number.';
    }
    
    alert('Payment Error: ' + errorMessage);
  };

  const handlePaymentCancel = () => {
    console.log('Payment cancelled');
    setShowPaymentModal(false);
    // Redirect to cancel page
    window.location.href = '/donation/cancel';
  };

  const handleModalSuccess = (_data) => {
    setShowPaymentModal(false);
    // Redirect to success page with transaction data
    window.location.href = '/donation/success';
  };

  const handleModalError = (error) => {
    setShowPaymentModal(false);
    handlePaymentError(error);
  };

  return (
    <>
      <SEO 
        title="Donate - WeFlare"
        description="Support WeFlare's mission to empower African youth through education, health, and empowerment initiatives."
        keywords="donate, WeFlare, youth empowerment, African youth, donation, support"
      />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Header */}
            <div className="text-center mb-5">
              <div className="mb-3">
                <span className="badge bg-primary-light text-primary px-3 py-2 rounded-pill">
                  <i className="bi bi-heart me-2"></i>
                  Make a Difference
                </span>
              </div>
              <h1 className="text-display mb-4" style={{fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--gray-800)'}}>
                Support Our Mission
              </h1>
              <p className="lead text-muted mb-4" style={{maxWidth: '600px', margin: '0 auto'}}>
                Your donation helps us empower African youth through education, health initiatives, and community programs. Every contribution makes a real impact.
              </p>
            </div>

            {/* Donation Form */}
            <div className="card-modern p-4 p-md-5">
              {/* Currency & Amount Selection */}
              <div className="mb-4">
                <h4 className="text1 mb-3" style={{color: 'var(--gray-800)'}}>
                  <span className="badge bg-primary text-white rounded-circle me-2">1</span>
                  Choose Currency & Amount
                </h4>
                
                {/* Currency Toggle */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Select Currency:</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${selectedCurrency === 'USD' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleCurrencyToggle('USD')}
                    >
                      <i className="bi bi-currency-dollar me-2"></i>
                      USD (US Dollars)
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedCurrency === 'KES' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleCurrencyToggle('KES')}
                    >
                      <i className="bi bi-cash me-2"></i>
                      KES (Kenyan Shillings)
                    </button>
                  </div>
                  <small className="text-muted">
                    {selectedCurrency === 'USD' 
                      ? 'Best for international donors (PayPal supported)' 
                      : 'Best for Kenyan donors (M-Pesa supported)'}
                  </small>
                </div>

                {/* Amount Selection */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Choose Amount ({selectedCurrency}):</label>
                  <div className="row g-3 mb-3">
                    {predefinedAmounts[selectedCurrency].map((amount) => (
                      <div key={amount} className="col-6 col-md-4">
                        <button
                          className={`btn w-100 ${selectedAmount === amount ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => handleAmountSelect(amount)}
                        >
                          {selectedCurrency} {amount.toLocaleString()}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom Amount */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Or enter custom amount:</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      {selectedCurrency === 'USD' ? '$' : 'KES'}
                    </span>
                    <input
                      type="number"
                      className="form-control"
                      placeholder={`Enter amount in ${selectedCurrency}`}
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      min="1"
                      step={selectedCurrency === 'USD' ? '0.01' : '1'}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="mb-4">
                <h4 className="text1 mb-3" style={{color: 'var(--gray-800)'}}>
                  <span className="badge bg-primary text-white rounded-circle me-2">2</span>
                  Payment Methods
                </h4>
                
                {getFinalAmount() ? (
                  <div className="alert alert-success mb-4">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-check-circle fs-4 me-3 text-success"></i>
                      <div>
                        <h6 className="alert-heading">Ready to Donate!</h6>
                        <p className="mb-0">
                          <strong>Amount: {selectedCurrency} {getFinalAmount().toLocaleString()}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info mb-4">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-info-circle fs-4 me-3 text-primary"></i>
                      <div>
                        <h6 className="alert-heading">Select an Amount</h6>
                        <p className="mb-0">Please choose a donation amount to proceed with payment options.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Buttons */}
                {getFinalAmount() && (
                  <div className="row g-4">
                    {/* PayPal Payment */}
                    {selectedCurrency === 'USD' && (
                      <div className="col-md-6">
                        <div className="payment-section">
                          <h5 className="mb-3">
                            <i className="bi bi-paypal text-primary me-2"></i>
                            PayPal Payment
                          </h5>
                          <p className="text-muted small mb-3">
                            Secure payment with PayPal. Credit and debit cards accepted.
                          </p>
                          <PayPalScriptProvider options={paypalInitialOptions}>
                            <PayPalButton
                              amount={getFinalAmount()}
                              currency={selectedCurrency}
                              onSuccess={handlePaymentSuccess}
                              onError={handlePaymentError}
                              onCancel={handlePaymentCancel}
                            />
                          </PayPalScriptProvider>
                        </div>
                      </div>
                    )}

                    {/* M-Pesa Payment */}
                    {selectedCurrency === 'KES' && (
                      <div className="col-md-6">
                        <div className="payment-section">
                          <h5 className="mb-3">
                            <i className="bi bi-phone text-success me-2"></i>
                            M-Pesa Payment
                          </h5>
                          <p className="text-muted small mb-3">
                            Pay with M-Pesa mobile money. Enter your phone number below.
                          </p>
                          
                          {/* Phone Number Input */}
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Phone Number:</label>
                            <div className="input-group">
                              <span className="input-group-text">+254</span>
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="7XXXXXXXX"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                maxLength="9"
                              />
                            </div>
                            <small className="text-muted">
                              Enter your Safaricom number (e.g., 712345678)
                            </small>
                          </div>

                          <MpesaButton
                            amount={getFinalAmount()}
                            phoneNumber={phoneNumber ? `254${phoneNumber}` : ''}
                            currency={selectedCurrency}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                            disabled={!phoneNumber || phoneNumber.length < 9}
                          />
                        </div>
                      </div>
                    )}

                    {/* Both currencies available */}
                    {selectedCurrency === 'USD' && (
                      <div className="col-md-6">
                        <div className="payment-alternative">
                          <h6 className="text-muted mb-2">Prefer M-Pesa?</h6>
                          <p className="text-muted small mb-3">
                            Switch to KES to pay with M-Pesa mobile money.
                          </p>
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleCurrencyToggle('KES')}
                          >
                            <i className="bi bi-phone me-1"></i>
                            Switch to KES
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedCurrency === 'KES' && (
                      <div className="col-md-6">
                        <div className="payment-alternative">
                          <h6 className="text-muted mb-2">Prefer PayPal?</h6>
                          <p className="text-muted small mb-3">
                            Switch to USD to pay with PayPal or credit card.
                          </p>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleCurrencyToggle('USD')}
                          >
                            <i className="bi bi-paypal me-1"></i>
                            Switch to USD
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Security Note */}
              <div className="text-center">
                <small className="text-muted">
                  <i className="bi bi-shield-check me-1"></i>
                  Your donation will be secure and encrypted. We never store payment information.
                </small>
              </div>
            </div>

            {/* Impact Section */}
            <div className="row mt-5 g-4">
              <div className="col-md-4 text-center">
                <div className="impact-card p-4">
                  <i className="bi bi-mortarboard fs-1 text-primary mb-3"></i>
                  <h5>
                    {selectedCurrency === 'USD' ? '$25' : 'KES 3,250'}
                  </h5>
                  <p className="text-muted">Provides educational materials for one student</p>
                  {selectedCurrency === 'USD' && (
                    <small className="text-muted">≈ KES 3,250</small>
                  )}
                  {selectedCurrency === 'KES' && (
                    <small className="text-muted">≈ $25</small>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-center">
                <div className="impact-card p-4">
                  <i className="bi bi-heart-pulse fs-1 text-success mb-3"></i>
                  <h5>
                    {selectedCurrency === 'USD' ? '$50' : 'KES 6,500'}
                  </h5>
                  <p className="text-muted">Supports health programs for 5 youth</p>
                  {selectedCurrency === 'USD' && (
                    <small className="text-muted">≈ KES 6,500</small>
                  )}
                  {selectedCurrency === 'KES' && (
                    <small className="text-muted">≈ $50</small>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-center">
                <div className="impact-card p-4">
                  <i className="bi bi-people fs-1 text-warning mb-3"></i>
                  <h5>
                    {selectedCurrency === 'USD' ? '$100' : 'KES 13,000'}
                  </h5>
                  <p className="text-muted">Funds a complete mentorship program</p>
                  {selectedCurrency === 'USD' && (
                    <small className="text-muted">≈ KES 13,000</small>
                  )}
                  {selectedCurrency === 'KES' && (
                    <small className="text-muted">≈ $100</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Modal */}
      <PaymentStatusModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentData={paymentData}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />
    </>
  );
};

export default DonatePage;